import { prefetch as quicklinkPrefetch } from 'quicklink'

let hoverListeners = new WeakMap()
const prefetchedUrls = new Set()

/**
 * Check if a link should be prefetched
 * @param {HTMLElement} link - The link element
 * @returns {boolean}
 */
const shouldPrefetch = (link) => {
  const href = link.getAttribute('href')
  if (!href || href.startsWith('#') || link.hasAttribute('data-skip-prefetch')) {
    return false
  }

  // Check if external link
  try {
    const url = new URL(href, window.location.href)
    if (url.origin !== window.location.origin) return false
  } catch {
    return false
  }

  // Check if download link
  const downloadExtensions = ['.pdf', '.zip', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif']
  if (downloadExtensions.some(ext => href.toLowerCase().endsWith(ext))) {
    return false
  }

  return true
}

/**
 * Normalize a URL to absolute format
 * @param {string} url - URL to normalize (can be relative or absolute)
 * @returns {string|null} - Normalized absolute URL or null if invalid
 */
const normalizeUrl = (url) => {
  try {
    return new URL(url, window.location.href).href
  } catch {
    return null
  }
}

/**
 * Prefetch a URL using quicklink
 * @param {string} url - URL to prefetch (can be relative or absolute)
 */
const prefetchUrl = (url) => {
  const absoluteUrl = normalizeUrl(url)
  if (!absoluteUrl) return

  // Check if already prefetched
  if (prefetchedUrls.has(absoluteUrl)) {
    return
  }

  try {
    // Use quicklink's prefetch method
    quicklinkPrefetch([absoluteUrl])
    
    // Mark as prefetched
    prefetchedUrls.add(absoluteUrl)
  } catch (error) {
    // Silently fail if prefetch fails
    console.debug('Prefetch failed:', url, error)
  }
}

/**
 * Initialize prefetch with hover listeners
 */
export const initPrefetch = () => {
  attachHoverListeners()
}

/**
 * Attach hover event listeners to links for prefetching
 */
const attachHoverListeners = () => {
  const links = document.querySelectorAll('a[href]')
  
  links.forEach(link => {
    // Skip if already has listener
    if (hoverListeners.has(link)) return
    
    // Skip if should be ignored
    if (!shouldPrefetch(link)) return

    const href = link.getAttribute('href')
    const absoluteUrl = normalizeUrl(href)
    let hoverTimeout = null
    const HOVER_DELAY = 100 // Small delay to avoid prefetching on accidental hovers

    const handleMouseEnter = () => {
      // Skip if already prefetched
      if (absoluteUrl && prefetchedUrls.has(absoluteUrl)) return
      
      hoverTimeout = setTimeout(() => {
        prefetchUrl(href)
      }, HOVER_DELAY)
    }

    const handleMouseLeave = () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout)
        hoverTimeout = null
      }
    }

    link.addEventListener('mouseenter', handleMouseEnter, { passive: true })
    link.addEventListener('mouseleave', handleMouseLeave, { passive: true })
    
    // Store listener info
    hoverListeners.set(link, { handleMouseEnter, handleMouseLeave })
  })
}

/**
 * Refresh prefetch listeners after Highway navigation
 * Call this in onEnterCompleted to reattach listeners to new DOM
 */
export const refreshPrefetch = () => {
  // Clear existing listeners from removed DOM elements
  // (WeakMap will automatically clean up when elements are removed)
  
  // Attach listeners to new links
  attachHoverListeners()
}

/**
 * Remove prefetch listeners from a specific link
 * @param {HTMLElement} link - The link element
 */
const removeLinkListener = (link) => {
  const listenerInfo = hoverListeners.get(link)
  if (listenerInfo) {
    link.removeEventListener('mouseenter', listenerInfo.handleMouseEnter)
    link.removeEventListener('mouseleave', listenerInfo.handleMouseLeave)
    hoverListeners.delete(link)
  }
}

/**
 * Destroy all prefetch listeners
 */
export const destroyPrefetch = () => {
  // Get all links that have listeners
  const links = document.querySelectorAll('a[href]')
  links.forEach(link => {
    if (hoverListeners.has(link)) {
      removeLinkListener(link)
    }
  })
}

/**
 * Clear the prefetched URLs Set
 * Useful if you want to reset prefetch tracking
 */
export const clearPrefetchedUrls = () => {
  prefetchedUrls.clear()
}

/**
 * Get the Set of prefetched URLs (for debugging)
 * @returns {Set<string>} - Set of prefetched URLs
 */
export const getPrefetchedUrls = () => {
  return prefetchedUrls
}

